import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, getInitials } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { MessageCircle, Loader2, Plus } from 'lucide-react';
import NewChatModal from '../components/chat/NewChatModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatListPage() {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);

  const fetchChats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChats(response.data);
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const getChatName = (chat) => {
    if (chat.is_group) return chat.group_name;
    const other = chat.participants.find(p => p.id !== user?.id);
    return other?.display_name || other?.username || 'Unknown';
  };

  const getChatAvatar = (chat) => {
    if (chat.is_group) return chat.group_avatar;
    const other = chat.participants.find(p => p.id !== user?.id);
    return other?.avatar || '';
  };

  const handleChatCreated = (chat) => {
    setShowNewChat(false);
    navigate(`/chat/${chat.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="chat-list-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">{tr('chats')}</h1>
          </div>
          <Button 
            size="icon" 
            className="rounded-full"
            onClick={() => setShowNewChat(true)}
            data-testid="new-chat-btn"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Chat List */}
      <div className="divide-y divide-border/40">
        {chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <MessageCircle className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-center">{tr('noChats')}</p>
            <Button 
              className="mt-4 rounded-full"
              onClick={() => setShowNewChat(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {tr('newChat')}
            </Button>
          </div>
        ) : (
          chats.map(chat => (
            <Link
              key={chat.id}
              to={`/chat/${chat.id}`}
              className="flex items-center gap-3 p-4 hover:bg-secondary/30 transition-colors"
              data-testid={`chat-item-${chat.id}`}
            >
              <div className="relative">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={getChatAvatar(chat)} alt={getChatName(chat)} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(getChatName(chat))}
                  </AvatarFallback>
                </Avatar>
                {chat.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-bold">
                    {chat.unread_count > 9 ? '9+' : chat.unread_count}
                  </span>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold truncate">{getChatName(chat)}</h3>
                  {chat.last_message && (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTimeAgo(chat.last_message.created_at, language)}
                    </span>
                  )}
                </div>
                {chat.last_message && (
                  <p className="text-sm text-muted-foreground truncate mt-1">
                    {chat.last_message.sender_id === user?.id ? 'Ou: ' : ''}
                    {chat.last_message.content}
                  </p>
                )}
              </div>
            </Link>
          ))
        )}
      </div>

      {/* New Chat Modal */}
      <NewChatModal 
        open={showNewChat} 
        onClose={() => setShowNewChat(false)}
        onChatCreated={handleChatCreated}
      />
    </div>
  );
}
